import React, { useEffect, createContext, ReactNode, useState } from 'react'
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail } from 'firebase/auth'
import { useSetAtom } from 'jotai'
import { userAtom } from '../state/userAtom'
import { auth } from '../services/firebase.service'
import { getFirestore, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { UserDetails } from '../types/userDetails.types'
import { FirebaseCollections } from '../constants/firebase.constants'

interface AuthContextType {
  login: (email: string, password: string) => Promise<void>
  resetPassword: (email: string) => Promise<void>
  logout: () => Promise<void>
  loading: boolean
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const setAtomUser = useSetAtom(userAtom)
  const [loading, setLoading] = useState(true)
  const db = getFirestore()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userDoc = await getDoc(doc(db, FirebaseCollections.USERS, firebaseUser.uid))
        if (userDoc.exists()) {
          setAtomUser({ ...firebaseUser, ...userDoc.data() } as UserDetails)
        } else {
          setAtomUser(firebaseUser)
        }
      } else {
        setAtomUser(null)
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [setAtomUser, db])

  // --------------------------- LOGIN ---------------------------
  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password)
    const firebaseUser = userCredential.user
    const userDoc = await getDoc(doc(db, FirebaseCollections.USERS, firebaseUser.uid))

    if (userDoc.exists()) {
      const userData = userDoc.data()

      // Update lastLoginAt timestamp
      await updateDoc(doc(db, FirebaseCollections.USERS, firebaseUser.uid), {
        lastLoginAt: serverTimestamp(),
      })

      // Pass the old data to the atom (userData contains the old lastLoginAt)
      setAtomUser({
        ...firebaseUser,
        ...userData, // This contains the old lastLoginAt for comparison
      })
    } else {
      setAtomUser(firebaseUser)
    }
  }

  // --------------------------- LOGOUT ---------------------------
  const logout = async () => {
    await signOut(auth)
    setAtomUser(null)
  }

  // --------------------------- RESET PASSWORD ---------------------------
  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email.trim())
  }

  return <AuthContext.Provider value={{ login, logout, resetPassword, loading }}>{children}</AuthContext.Provider>
}
